import AsyncStorage from '@react-native-async-storage/async-storage';
import { WEB_SOCKET_URL, IMAGE_PATH, ORGANIZATION_TYPE } from '@env';
import moment, { min } from 'moment';
import { Platform, Dimensions, useWindowDimensions } from 'react-native';
import * as Sentry from 'sentry-expo';
import 'moment-timezone';
import { deleteFirebaseToken } from '../../firebase';
import { setLightTheme } from '../store/slice/themeSlice';
import { setUser } from '../store/slice/userSlice';
import { setRole } from '../store/slice/roleSlice';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

export const weekDayNameList = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

export const removeItemLocalStorage = async (key) =>
  await AsyncStorage.removeItem(key);

export const getToken = async () => await AsyncStorage.getItem('token');

export const setToken = async (token) =>
  await AsyncStorage.setItem('token', token);

export const setReferrar = async (data) =>
  await AsyncStorage.setItem('referrer', data);

export const setFcmToken = async (token) =>
  await AsyncStorage.setItem('fcm_token', token);

export const getFcmToken = async () => await AsyncStorage.getItem('fcm_token');

export const getReferrar = async () => await AsyncStorage.getItem('referrer');

export const clearStorage = async () => await AsyncStorage.clear();

export const setQueueJoinId = async (id) =>
  await AsyncStorage.setItem('uqid', id);

export const setUserInterestQueueParam = async (params) =>
  await AsyncStorage.setItem('userInterestQueueParma', params);

export const getQueueJoinId = async () => await AsyncStorage.getItem('uqid');

export const setEmailVerificationToken = async (token) =>
  await AsyncStorage.setItem('email_verification_token', token);

export const getEmailVerificationToken = async () =>
  await AsyncStorage.getItem('email_verification_token');

export const getUserInterestQueueParam = async () =>
  await AsyncStorage.getItem('userInterestQueueParma');

export const getHasShownFirstTimeModal = async () =>
  await AsyncStorage.getItem('hasShownFirstTimeModal');

export const setHasShownFirstTimeModal = async (value) =>
  await AsyncStorage.setItem('hasShownFirstTimeModal', value);

export const showDate = (date) =>
  date ? moment(date).format('DD MMM YYYY') : '';

export const showTime = (time) =>
  time ? moment(time, ['HH:mm']).format('hh:mm A') : '';

export const showDateUTC = (date) =>
  date ? moment.utc(date).format('DD MMM YYYY') : '';

export const showDateTime = (date) =>
  date ? moment(date).format('DD MMM YYYY hh:mm A') : '';

export const showDateTimeUTCtoLocal = (date) =>
  date ? moment.utc(date).local().format('DD MMM YYYY hh:mm A') : '';

export const showDateTimeCustom = (date) =>
  date ? moment(date).format('MMMM D, YYYY - h:mm A') : '';

// Check if a date is today in a specific timezone
export const isDateTodayInTimezone = (date, timezone) => {
  if (!date) {
    throw new Error('Date is required for timezone comparison');
  }

  if (!timezone) {
    throw new Error('Timezone is required for date comparison');
  }

  try {
    // Get current time in the specified timezone
    const nowInTimezone = moment.tz(new Date(), timezone);

    // Convert the input date to the specified timezone
    const dateInTimezone = moment.tz(date, timezone);

    // Compare year, month, and day
    return (
      nowInTimezone.format('YYYY-MM-DD') === dateInTimezone.format('YYYY-MM-DD')
    );
  } catch (error) {
    // If timezone is not recognized by moment, it will throw
    throw new Error(
      `Invalid or unrecognized timezone: ${timezone}. Error: ${error.message}`
    );
  }
};

export const setRoleLocalStorage = async (role) =>
  await AsyncStorage.setItem('role', role);

export const getRoleLocalStorage = async () =>
  await AsyncStorage.getItem('role');

export const getOnlyTimeAmPMFormat = (datetime, timezone = null) =>
  datetime ? moment(datetime).format('hh:mm A') : '';

export const getBatchTimeDisplay = (datetime, timezone = null) => {
  if (!datetime) return '';

  // For batch queues, the API returns time with 'Z' suffix but it's actually already in local time
  // So we need to parse it without timezone conversion
  const datetimeStr = datetime.toString();
  const cleanedDatetime = datetimeStr.endsWith('Z')
    ? datetimeStr.slice(0, -1)
    : datetimeStr;

  // Parse as local time (not UTC)
  const localTime = moment(cleanedDatetime);

  // Check if this is end of day (23:59)
  const timeString = localTime.format('HH:mm');
  if (timeString === '23:59') {
    return 'End of Day';
  }

  return localTime.format('hh:mm A');
};

export const showTimeZone = (timezone) => {
  if (!timezone) return 'UTC';

  try {
    let abbr = moment.tz(timezone).zoneAbbr();

    if (
      abbr === timezone ||
      abbr.includes('-') ||
      abbr.includes('+') ||
      abbr === 'GMT'
    ) {
      const offset = moment.tz(timezone).format('Z');
      return `(GMT${offset})`;
    }

    return abbr;
  } catch (error) {
    return timezone;
  }
};

export const getTimeDifferenceInSeconds = (targetTimeString, timezone) => {
  const now = moment().tz(timezone);
  const estimatedTime = moment.tz(targetTimeString, timezone);
  const diffInSeconds = estimatedTime.diff(now, 'seconds');
  return diffInSeconds;
};

export const setThemeLocalStorage = async (value) =>
  await AsyncStorage.setItem('isLightTheme', value);

export const getThemeLocalStorage = async () =>
  await AsyncStorage.getItem('isLightTheme');

export const getWebSocketUrl = () => {
  if (Platform.OS === 'web') {
    let wsProtocol = location.protocol === 'http:' ? 'ws' : 'wss';
    let hostName = WEB_SOCKET_URL;
    return wsProtocol + '://' + hostName + '/ws';
  } else {
    let wsProtocol = 'wss';
    let hostName = WEB_SOCKET_URL;
    return wsProtocol + '://' + hostName + '/ws';
  }
};

export const getImageUrl = (fileName, dir = '') => {
  return IMAGE_PATH + dir + fileName;
};

export const getAvatarLabel = (name) => {
  let label = 'N/A';
  if (name) {
    let nameList = name.split(' ');
    label = nameList[0].substring(0, 1).toUpperCase();
    if (nameList.length > 1) {
      label += nameList[1].substring(0, 1).toUpperCase();
    }
  }
  return label;
};

export const goBackHandler = (navigation) => {
  // if (navigation.getState().routes.length > 1) {
  //     navigation.goBack();
  //   } else {
  //     navigation.push('DashboardScreen');
  //   }
  if (Platform.OS === 'web') {
    window.history.back();
  } else {
    navigation.goBack();
  }
};

export const errorLogOnServer = (msg) => {
  if (Platform.OS === 'web') {
    Sentry.Browser.captureException(msg);
  } else {
    Sentry.Native.captureException(msg);
  }
};

export const getWeekListForQueueCard = (weekList, holidayList) => {
  let dateArrayList = [];
  let holidayFilter =
    holidayList && holidayList.length > 0
      ? holidayList.filter(
          (x) =>
            moment(moment().format('YYYY-MM-DD')).isSameOrBefore(x.date) &&
            x.serving_capacity === 0
        )
      : [];

  for (var i = 0; i < 7; i++) {
    let date = moment().add(i, 'days');
    let dayNumber = date.isoWeekday() - 1;
    let isHoliday = false;
    let isDayOff = true;
    let start_time = '';
    let end_time = '';
    let isAvailable = false;
    if (
      holidayFilter &&
      holidayFilter.filter((x) => date.format('YYYY-MM-DD') == x.date).length >
        0
    ) {
      isHoliday = true;
    }

    if (weekList && weekList.length > 0 && isHoliday === false) {
      let filterWeekList = weekList.filter((x) => x.day_number === dayNumber);
      if (filterWeekList.length > 0) {
        isDayOff = false;
        if (filterWeekList[0]?.start_time) {
          start_time = filterWeekList[0]?.start_time;
        }
        if (filterWeekList[0]?.end_time) {
          end_time = filterWeekList[0]?.end_time;
          let beginningTime = moment(
            date.format('YYYY-MM-DD') + ' ' + filterWeekList[0]?.end_time
          );
          var endTime = moment();
          isAvailable = beginningTime.isAfter(endTime);
        }
      }
    }
    dateArrayList[i] = {
      date,
      text: date.format('dddd').substring(0, 1),
      dayNumber,
      isHoliday,
      isDayOff,
      start_time,
      end_time,
      isAvailable,
    };
  }

  let availableDayID = '';
  let dateArrayIsAvailableList = dateArrayList.filter(
    (x) => x.isAvailable === true
  );
  if (dateArrayIsAvailableList.length > 0) {
    availableDayID = dateArrayIsAvailableList[0].dayNumber;
  }

  if (availableDayID !== '') {
    dateArrayList = dateArrayList.map((item) => {
      item.isAvailable = false;
      if (availableDayID === item.dayNumber) {
        item.isAvailable = true;
      }
      return item;
    });
  }
  dateArrayList.sort(function (a, b) {
    return a.dayNumber - b.dayNumber;
  });

  return dateArrayList;
};

export const getDisabledHolidayListAdvancedBooking = (
  weekData,
  holidayData,
  allowAdvancedDay
) => {
  let holidayList = holidayData.map((i) => new Date(i.date));
  let weekList = weekData;
  const results = [];
  let start = moment().format('YYYY-MM-DD');
  let end = moment().add(parseInt(allowAdvancedDay), 'd').format('YYYY-MM-DD');
  let weekdayDisabled = [];
  for (let i = 0; i < 7; i++) {
    let filterWeekList = weekList.filter((x) => x.id === i);
    if (filterWeekList.length === 0) {
      weekdayDisabled.push(i);
    }
  }

  while (moment(start) <= moment(end)) {
    if (weekdayDisabled.includes(moment(start).isoWeekday() - 1)) {
      results.push(new Date(start));
    }
    start = moment(start).add(1, 'days').format('YYYY-MM-DD');
  }
  return holidayList.concat(results);
};

export const appointmentColorInfo = () => {
  return [
    {
      label: 'Unconfirmed',
      value: 'Unconfirmed',
    },
    {
      label: 'Confirmed',
      value: 'Confirmed',
    },
    {
      label: 'Buffer',
      value: 'Buffer',
    },
    {
      label: 'Serving',
      value: 'Serving',
    },
    {
      label: 'Completed',
      value: 'Completed',
    },
    {
      label: 'Cancel / Removed',
      value: 'Removed',
    },
  ];
};

export const livePlanColorInfo = () => {
  return [
    {
      label: 'Waiting',
      value: 'Waiting',
    },
    {
      label: 'Serving',
      value: 'Serving',
    },
    {
      label: 'Skipped',
      value: 'Skipped',
    },
  ];
};

export const normalizeFontSize = (size) => {
  const BASE_WIDTH = 1920;
  const BASE_HEIGHT = 1080;

  const scale = Math.min(
    SCREEN_WIDTH / BASE_WIDTH,
    SCREEN_HEIGHT / BASE_HEIGHT
  );
  return Math.max(Math.round(size * scale), size * 0.5); // Ensure minimum font size doesn't drop below 50% of original
};

export const updateNormalizeFontSize = () => {
  const { width, height } = Dimensions.get('window');
  SCREEN_WIDTH = width;
  SCREEN_HEIGHT = height;
};

export const getIsMobileDevice = () => {
  // Get window width using window.innerWidth or similar method
  const width = window.innerWidth;
  return width <= 768; // Now considers anything below 768px as mobile
};

export const isTabletDevice = () => {
  const width = window.innerWidth;
  return width > 768 && width <= 1024;
};

// export const generateTimeSlots = (start, end, interval) => {
//   const timeSlots = [];
//   let current = convertTo24Hour(start);
//   const endTime = convertTo24Hour(end);

//   while (current < endTime) {
//     const startTime = formatAMPM(current);
//     current.setMinutes(current.getMinutes() + interval);
//     const endTime = formatAMPM(current);
//     timeSlots.push({ start_time: startTime, end_time: endTime });
//   }
//   return timeSlots;
// };

export const generateTimeSlots = (start, end, interval) => {
  const timeSlots = [];
  let current = new Date();
  current.setHours(
    parseInt(start.split(':')[0]),
    parseInt(start.split(':')[1]),
    0
  );

  const endTime = new Date();
  endTime.setHours(parseInt(end.split(':')[0]), parseInt(end.split(':')[1]), 0);

  while (current < endTime) {
    const startTime = `${current.getHours().toString().padStart(2, '0')}:${current.getMinutes().toString().padStart(2, '0')}`;
    current.setMinutes(current.getMinutes() + interval);
    const endTimeSlot = `${current.getHours().toString().padStart(2, '0')}:${current.getMinutes().toString().padStart(2, '0')}`;
    timeSlots.push({ start_time: startTime, end_time: endTimeSlot });
  }
  return timeSlots;
};

export const convertTo24Hour = (time) => {
  const [timePart, modifier] = time.split(' ');
  let [hours, minutes] = timePart.split(':').map(Number);

  if (modifier === 'PM' && hours !== 12) {
    hours += 12;
  } else if (modifier === 'AM' && hours === 12) {
    hours = 0;
  }

  return new Date(1970, 0, 1, hours, minutes);
};

export const formatAMPM = (date) => {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;
  const strTime = `${hours}:${minutes} ${ampm}`;
  return strTime;
};

export const getQueueJoinShareLink = (id) => {
  return location.protocol + '//' + location.host + '/join-queue/' + id;
};

export const getBusinessJoinShareLink = (businessId) => {
  const hostname = window.location.origin;
  return `${hostname}/business/${businessId}/join`;
};

export const getTrackingLink = (id) => {
  return (
    location.protocol +
    '//' +
    location.host +
    '/track-joined-queue-position/' +
    id
  );
};

export const getISOTimeFormat = (time) =>
  time ? moment(time, ['h:mm A']).format('HH:mm') : '';

export const convertHolidayResponseToNeedHolidayResponse = (
  holidayResponse
) => {
  let needHolidayResponse = [];
  for (const [date, details] of Object.entries(holidayResponse)) {
    let holidayObj = {
      date: date,
      serving_capacity: details?.serving_capacity
        ? details?.serving_capacity
        : 0,
      error: '',
      batch_size: details?.batch_size ? details?.batch_size : 0,
      batch_slot_duration: details?.batch_slot_duration
        ? details?.batch_slot_duration
        : 0,
      batchSizeError: '',
      slotDurationError: '',
    };
    needHolidayResponse.push(holidayObj);
  }
  return needHolidayResponse;
};

export const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

export const convertWeekdayResponseToNeedResponse = (weekdayResponse) => {
  const convertTo12HourFormat = (time24) => {
    const [hour, minute] = time24.split(':');
    const suffix = +hour < 12 ? 'AM' : 'PM';
    const hour12 = ((+hour + 11) % 12) + 1;
    return `${hour12}:${minute} ${suffix}`;
  };

  let needResponse = [];

  for (const [day, details] of Object.entries(weekdayResponse)) {
    let dayObj = {
      id: parseInt(day),
      label: parseInt(day),
      short: parseInt(day),
      value: details?.is_closed === true ? false : true,
      isDisabled: false,
      startTime:
        details?.shifts[0] && details.shifts[0].start
          ? convertTo12HourFormat(details.shifts[0].start)
          : '',
      endTime:
        details?.shifts[0] && details.shifts[0].end
          ? convertTo12HourFormat(details.shifts[0].end)
          : '',
      startTimeError: '',
      endTimeError: '',
      breakTimeList: [],
    };

    for (const breakTime of details.break_times) {
      dayObj.breakTimeList.push({
        id: generateUUID(),
        startBreakTime: convertTo12HourFormat(breakTime.start),
        endBreakTime: convertTo12HourFormat(breakTime.end),
        startBreakTimeError: '',
        endBreakTimeError: '',
      });
    }

    needResponse.push(dayObj);
  }
  return needResponse;
};

export const getCurrentTimeZone = () => {
  return moment.tz.guess();
};

/**
 * Calculates if queue end time aligns with slot duration
 * @param {string} startTime - Queue start time in "hh:mm A" format
 * @param {string} endTime - Queue end time in "hh:mm A" format
 * @param {number} slotDuration - Duration of each slot in minutes
 * @returns {Object} - Contains isAligned and lastSlotTime
 */
export const calculateQueueTimeAlignment = (
  startTime,
  endTime,
  slotDuration
) => {
  // Special handling for "11:59 PM" end time (end of day)
  if (endTime === '11:59 PM') {
    return {
      isAligned: true,
      lastSlotTime: '11:59 PM',
      isEndOfDay: true,
    };
  }

  // Convert times to minutes since midnight
  const start = moment(startTime, 'hh:mm A');
  const end = moment(endTime, 'hh:mm A');

  // Calculate total minutes
  const totalMinutes = end.diff(start, 'minutes');

  // Calculate remainder
  const remainder = totalMinutes % slotDuration;

  if (remainder === 0) {
    return {
      isAligned: true,
      lastSlotTime: end.format('hh:mm A'),
    };
  }

  // Calculate actual end time for last complete slot
  const adjustedEndTime = moment(end).subtract(remainder, 'minutes');

  return {
    isAligned: false,
    lastSlotTime: adjustedEndTime.format('hh:mm A'),
    remainder,
  };
};

export const handleLogout = async ({ logout, i18n, dispatch, fcmToken }) => {
  if (fcmToken) {
    await logout({ device_token: fcmToken });
  }
  await removeItemLocalStorage('fcm_token');
  await removeItemLocalStorage('token');
  await removeItemLocalStorage('role');
  await removeItemLocalStorage('lang');
  await removeItemLocalStorage('isLightTheme');
  await removeItemLocalStorage('hasShownFirstTimeModal');
  sessionStorage.clear();
  await deleteFirebaseToken();
  i18n.changeLanguage('en');
  dispatch(setLightTheme(false));
  dispatch(setUser(null));
  dispatch(setRole('user'));
};

export const getFontSize = (desktop, tablet, mobile) => {
  const width = window.innerWidth;
  if (width > 1024) {
    return desktop;
  } else if (width > 768) {
    return tablet;
  } else {
    return mobile;
  }
};

export const getResponsiveValue = (desktop, tablet, mobile) => {
  const width = window.innerWidth;
  if (width > 1024) {
    return desktop;
  } else if (width > 768) {
    return tablet;
  } else {
    return mobile;
  }
};

// Common formatting functions used across transaction screens
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export const formatTime = (timeString) => {
  if (!timeString) return '';
  const timeParts = timeString.split(':');
  let hours = parseInt(timeParts[0]);
  const minutes = timeParts[1];
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'
  return `${hours}:${minutes} ${ampm}`;
};

// Get formatted transactions from API data for PhonePe transactions
export const getFormattedPhonePeTransactions = (transactionData) => {
  if (!transactionData || !transactionData.items) return [];

  return transactionData.items.map((item) => {
    // Convert UTC time to local time
    const localDateTime = item.added_at
      ? showDateTimeUTCtoLocal(item.added_at)
      : '';
    const dateParts = localDateTime.split(' ');
    const date = dateParts.slice(0, 3).join(' '); // "DD MMM YYYY"
    const time = dateParts.slice(3).join(' '); // "hh:mm AM/PM"

    return {
      id: item.id,
      date: date || '',
      time: time || '',
      amount: parseFloat(item.amount) || 0,
      transaction_type: item.transaction_type || 'Debit',
      status: item.status || 'unknown',
      currency: item.currency || 'INR',
      transaction_reference: item.transaction_reference || '',
      description:
        item.metainfo && item.metainfo.description
          ? item.metainfo.description
          : '',
      wallet_id: item.wallet_id,
      ref_id: item.ref_id || '',
    };
  });
};

// Get formatted transactions from API data for wallet transactions
export const getFormattedWalletTransactions = (transactionData) => {
  if (!transactionData || !transactionData.items) return [];

  return transactionData.items.map((item) => {
    // Convert UTC time to local time
    const localDateTime = item.added_at
      ? showDateTimeUTCtoLocal(item.added_at)
      : '';
    const dateParts = localDateTime.split(' ');
    const date = dateParts.slice(0, 3).join(' '); // "DD MMM YYYY"
    const time = dateParts.slice(3).join(' '); // "hh:mm AM/PM"

    return {
      id: item.id,
      date: date || '',
      time: time || '',
      amount: parseFloat(item.value || item.amount) || 0,
      type:
        item.type === 'Added'
          ? 'Added'
          : item.type === 'Spent'
            ? 'Spent'
            : item.type === 'credit'
              ? 'credit'
              : 'debit',
      currency: item.currency || 'INR',
      description:
        item.metainfo && item.metainfo.description
          ? item.metainfo.description
          : '',
      metainfo: item.metainfo || {},
      wallet_id: item.wallet_id,
    };
  });
};

// Set at build time from APP_VERSION (CI passes the release version).
export const getBuildVersion = () =>
  process.env.EXPO_WEBSITE_BUILD_VERSION || 'local';

export const getOrganizationType = () => {
  return ORGANIZATION_TYPE || 'organization';
};

export const getOrganizationTypeCapitalized = () => {
  const orgType = getOrganizationType();
  return orgType.charAt(0).toUpperCase() + orgType.slice(1);
};

// Queue Schedule Formatting Utilities

/**
 * Detects the type of schedule based on queue data
 * @param {Object} queue - Queue object from API
 * @returns {string} - 'simple', 'recurring', or 'complex'
 */
export const detectScheduleType = (queue) => {
  if (!queue?.schedule?.weekdays) return 'simple';

  const weekdays = queue.schedule.weekdays;
  const activeDays = Object.keys(weekdays).filter(
    (day) => !weekdays[day]?.is_closed
  );

  if (activeDays.length === 0) return 'simple';
  if (activeDays.length === 1) return 'recurring';

  // Check if all active days have same time
  const firstDayShifts = weekdays[activeDays[0]]?.shifts || [];
  const hasVaryingTimes = activeDays.some((day) => {
    const dayShifts = weekdays[day]?.shifts || [];
    if (dayShifts.length !== firstDayShifts.length) return true;
    return dayShifts.some((shift, index) => {
      const firstShift = firstDayShifts[index];
      return (
        !firstShift ||
        shift.start !== firstShift.start ||
        shift.end !== firstShift.end
      );
    });
  });

  return hasVaryingTimes ? 'complex' : 'recurring';
};

/**
 * Checks if queue has varying schedule across days
 * @param {Object} queue - Queue object from API
 * @returns {boolean} - True if schedule varies by day
 */
export const hasVaryingSchedule = (queue) => {
  return detectScheduleType(queue) === 'complex';
};

/**
 * Gets recurrence pattern description
 * @param {Object} queue - Queue object from API
 * @returns {string} - Human readable recurrence pattern
 */
export const getRecurrencePattern = (queue) => {
  if (!queue?.schedule?.weekdays) return '';

  const weekdays = queue.schedule.weekdays;
  const activeDays = Object.keys(weekdays)
    .filter((day) => !weekdays[day]?.is_closed)
    .map((day) => parseInt(day))
    .sort();

  if (activeDays.length === 0) return '';

  const dayNames = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];

  // Check if this is truly a recurring pattern or just a one-time/short-term event
  const hasStartDate = queue.start_date;
  const hasEndDate = queue.end_date;

  // If no date range, assume it's recurring (legacy behavior)
  if (!hasStartDate && !hasEndDate) {
    return getRecurringPatternDescription(activeDays, dayNames);
  }

  // If only start date, assume it's recurring from that date onwards
  if (hasStartDate && !hasEndDate) {
    return getRecurringPatternDescription(activeDays, dayNames);
  }

  // If only end date, assume it's recurring until that date
  if (!hasStartDate && hasEndDate) {
    return getRecurringPatternDescription(activeDays, dayNames);
  }

  // Both start and end dates exist - need to determine if it's recurring or one-time
  if (hasStartDate && hasEndDate) {
    const startDate = moment(queue.start_date);
    const endDate = moment(queue.end_date);
    const daysDiff = endDate.diff(startDate, 'days');

    // If it's less than 7 days, it's likely a one-time event
    if (daysDiff < 7) {
      return getOneTimePatternDescription(activeDays, dayNames);
    }

    // If it's exactly 7 days, check if it spans exactly one week
    if (daysDiff === 6) {
      // 6 days difference means 7 days total (inclusive)
      const startDayOfWeek = startDate.day(); // 0 = Sunday, 1 = Monday, etc.
      const endDayOfWeek = endDate.day();

      // Check if it spans from Monday to Sunday (0 to 6) or any 7-day period
      if (
        (startDayOfWeek === 1 && endDayOfWeek === 0) || // Mon to Sun
        (startDayOfWeek === 0 && endDayOfWeek === 6) || // Sun to Sat
        (startDayOfWeek <= endDayOfWeek && endDayOfWeek - startDayOfWeek === 6)
      ) {
        // Any 7-day span
        return getOneTimePatternDescription(activeDays, dayNames);
      }
    }

    // For longer periods, check if it's truly recurring by calculating weeks
    const weeksDiff = Math.ceil(daysDiff / 7);

    // If it's less than 2 weeks, it's likely a one-time event
    if (weeksDiff < 2) {
      return getOneTimePatternDescription(activeDays, dayNames);
    }

    // If it's 2+ weeks, it's recurring
    return getRecurringPatternDescription(activeDays, dayNames);
  }

  // Fallback to recurring pattern
  return getRecurringPatternDescription(activeDays, dayNames);
};

/**
 * Generates description for recurring patterns
 */
const getRecurringPatternDescription = (activeDays, dayNames) => {
  // All days
  if (activeDays.length === 7) return 'Runs every day';

  // Single day
  if (activeDays.length === 1) {
    return `Runs weekly, every ${dayNames[activeDays[0]]}`;
  }

  // Check for weekdays pattern (Mon-Fri)
  const weekdaysPattern = [0, 1, 2, 3, 4];
  if (
    activeDays.length === 5 &&
    activeDays.every((day, index) => day === weekdaysPattern[index])
  ) {
    return 'Runs on weekdays';
  }

  // Check for weekend pattern
  const weekendPattern = [5, 6];
  if (
    activeDays.length === 2 &&
    activeDays.every((day, index) => day === weekendPattern[index])
  ) {
    return 'Runs on weekends';
  }

  // Multiple specific days
  if (activeDays.length <= 3) {
    const daysList = activeDays
      .map((day) => dayNames[day].substring(0, 3))
      .join(', ');
    return `Runs weekly on ${daysList}`;
  }

  return 'Custom weekly schedule';
};

/**
 * Generates description for one-time/short-term patterns
 */
const getOneTimePatternDescription = (activeDays, dayNames) => {
  // All days
  if (activeDays.length === 7) return 'Runs every day';

  // Single day
  if (activeDays.length === 1) {
    return `Runs on ${dayNames[activeDays[0]]}`;
  }

  // Check for weekdays pattern (Mon-Fri)
  const weekdaysPattern = [0, 1, 2, 3, 4];
  if (
    activeDays.length === 5 &&
    activeDays.every((day, index) => day === weekdaysPattern[index])
  ) {
    return 'Runs on weekdays';
  }

  // Check for weekend pattern
  const weekendPattern = [5, 6];
  if (
    activeDays.length === 2 &&
    activeDays.every((day, index) => day === weekendPattern[index])
  ) {
    return 'Runs on weekends';
  }

  // Multiple specific days
  if (activeDays.length <= 3) {
    const daysList = activeDays
      .map((day) => dayNames[day].substring(0, 3))
      .join(', ');
    return `Runs on ${daysList}`;
  }

  return 'Custom schedule';
};

/**
 * Formats the complete schedule text for display
 * @param {Object} queue - Queue object from API
 * @returns {string} - Formatted schedule text
 */
export const formatScheduleText = (queue) => {
  const type = detectScheduleType(queue);
  const pattern = getRecurrencePattern(queue);
  const hasStartDate = queue.start_date;
  const hasEndDate = queue.end_date;

  // No schedule defined
  if (type === 'simple' && !hasStartDate && !hasEndDate) {
    return 'No schedule limit';
  }

  // Build the schedule description
  let scheduleText = '';

  if (pattern) {
    scheduleText = pattern;

    // Add date range information
    if (hasStartDate && hasEndDate) {
      const start = showDate(queue.start_date);
      const end = showDate(queue.end_date);
      if (start === end) {
        scheduleText += ` on ${start}`;
      } else {
        scheduleText += ` from ${start} to ${end}`;
      }
    } else if (hasStartDate) {
      scheduleText += ` from ${showDate(queue.start_date)} onwards`;
    } else if (hasEndDate) {
      scheduleText += ` until ${showDate(queue.end_date)}`;
    }
  } else {
    // No recurring pattern, just date range
    if (hasStartDate && hasEndDate) {
      const start = showDate(queue.start_date);
      const end = showDate(queue.end_date);
      if (start === end) {
        scheduleText = `${start} only`;
      } else {
        scheduleText = `From ${start} to ${end}`;
      }
    } else if (hasStartDate) {
      scheduleText = `From ${showDate(queue.start_date)} onwards`;
    } else if (hasEndDate) {
      scheduleText = `Until ${showDate(queue.end_date)}`;
    }
  }

  // For complex schedules with varying times, add a note
  if (type === 'complex' && scheduleText) {
    return scheduleText;
  }

  return scheduleText || 'No schedule limit';
};

/**
 * Formats date range for display
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {string} - Formatted date range
 */
export const formatDateRange = (startDate, endDate) => {
  if (!startDate || !endDate) return '';

  const start = showDate(startDate);
  const end = showDate(endDate);

  if (start === end) return start;
  return `${start} - ${end}`;
};

/**
 * Determines if queue has consistent time across all active days
 * @param {Object} queue - Queue object from API
 * @returns {boolean} - True if all active days have same time
 */
export const hasConsistentTime = (queue) => {
  if (!queue?.schedule?.weekdays) return true;

  const weekdays = queue.schedule.weekdays;
  const activeDays = Object.keys(weekdays).filter(
    (day) => !weekdays[day]?.is_closed
  );

  if (activeDays.length <= 1) return true;

  const firstDayShifts = weekdays[activeDays[0]]?.shifts || [];
  return activeDays.every((day) => {
    const dayShifts = weekdays[day]?.shifts || [];
    if (dayShifts.length !== firstDayShifts.length) return false;
    return dayShifts.every((shift, index) => {
      const firstShift = firstDayShifts[index];
      return (
        firstShift &&
        shift.start === firstShift.start &&
        shift.end === firstShift.end
      );
    });
  });
};

/**
 * Formats time display for queue card
 * @param {Object} queue - Queue object from API
 * @returns {Object} - {text: string, isVarying: boolean}
 */
export const formatTimeDisplay = (queue) => {
  // Check for legacy start_time/end_time first
  if (queue?.start_time && queue?.end_time) {
    const timeText = `${showTime(queue.start_time)} - ${showTime(queue.end_time)}`;
    const timezone = showTimeZone(queue.timezone);
    return {
      text: timezone ? `${timeText} ${timezone}` : timeText,
      isVarying: false,
    };
  }

  // Check schedule data
  if (!queue?.schedule?.weekdays) {
    return { text: 'Always open', isVarying: false };
  }

  const weekdays = queue.schedule.weekdays;
  const activeDays = Object.keys(weekdays).filter(
    (day) => !weekdays[day]?.is_closed
  );

  if (activeDays.length === 0) {
    return { text: 'Currently closed', isVarying: false };
  }

  if (hasConsistentTime(queue)) {
    const firstDay = weekdays[activeDays[0]];
    const shifts = firstDay?.shifts || [];
    if (shifts.length === 0) {
      return { text: 'Hours not set', isVarying: false };
    }

    // Single shift - show the time range
    if (shifts.length === 1) {
      const firstShift = shifts[0];
      const timeText = `${showTime(firstShift.start)} - ${showTime(firstShift.end)}`;
      const timezone = showTimeZone(queue.timezone);
      return {
        text: timezone ? `${timeText} ${timezone}` : timeText,
        isVarying: false,
      };
    }

    // Multiple shifts but consistent across days
    const timeRanges = shifts
      .map((shift) => `${showTime(shift.start)} - ${showTime(shift.end)}`)
      .join(', ');
    const timezone = showTimeZone(queue.timezone);
    return {
      text: timezone ? `${timeRanges} ${timezone}` : timeRanges,
      isVarying: false,
    };
  }

  // Time varies by day
  return {
    text: 'Time varies by day',
    isVarying: true,
  };
};

/**
 * Gets detailed weekly schedule for tooltip display
 * @param {Object} queue - Queue object from API
 * @returns {Array} - Array of {day: string, time: string, isClosed: boolean}
 */
export const getDetailedWeeklySchedule = (queue) => {
  const dayNames = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday',
  ];
  const schedule = [];

  if (!queue?.schedule?.weekdays) {
    return dayNames.map((day) => ({ day, time: '-', isClosed: true }));
  }

  const weekdays = queue.schedule.weekdays;
  const timezone = showTimeZone(queue.timezone);

  dayNames.forEach((dayName, index) => {
    const dayData = weekdays[index.toString()];

    if (!dayData || dayData.is_closed) {
      schedule.push({ day: dayName, time: 'Closed', isClosed: true });
      return;
    }

    const shifts = dayData.shifts || [];
    if (shifts.length === 0) {
      schedule.push({ day: dayName, time: 'Closed', isClosed: true });
      return;
    }

    if (shifts.length === 1) {
      const shift = shifts[0];
      const timeText = `${showTime(shift.start)} - ${showTime(shift.end)}`;
      schedule.push({
        day: dayName,
        time: timezone ? `${timeText} ${timezone}` : timeText,
        isClosed: false,
      });
    } else {
      // Multiple shifts - show as ranges
      const timeRanges = shifts
        .map((shift) => `${showTime(shift.start)} - ${showTime(shift.end)}`)
        .join(', ');
      schedule.push({
        day: dayName,
        time: timezone ? `${timeRanges} ${timezone}` : timeRanges,
        isClosed: false,
      });
    }
  });

  return schedule;
};

/**
 * Gets the next valid date based on queue schedule
 * @param {Object} queue - Queue object with schedule information
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @returns {string} - Next valid date in YYYY-MM-DD format
 */
export const getNextValidDate = (queue, currentDate) => {
  if (!queue?.schedule?.weekdays) {
    // No schedule restrictions, just return next day
    return moment(currentDate).add(1, 'day').format('YYYY-MM-DD');
  }

  const weekdays = queue.schedule.weekdays;
  const activeDays = Object.keys(weekdays)
    .filter((day) => !weekdays[day]?.is_closed)
    .map((day) => parseInt(day));

  if (activeDays.length === 0) {
    // No active days, just return next day
    return moment(currentDate).add(1, 'day').format('YYYY-MM-DD');
  }

  let nextDate = moment(currentDate).add(1, 'day');
  let attempts = 0;
  const maxAttempts = 14; // Prevent infinite loop, check up to 2 weeks

  while (attempts < maxAttempts) {
    const momentWeekday = nextDate.weekday();
    const pythonWeekday = (momentWeekday + 6) % 7; // Convert to Python weekday

    // Check if this day is active in the schedule
    if (activeDays.includes(pythonWeekday)) {
      // Check if date is within queue's date range
      const dateStr = nextDate.format('YYYY-MM-DD');

      if (queue.start_date && moment(dateStr).isBefore(queue.start_date)) {
        nextDate.add(1, 'day');
        attempts++;
        continue;
      }

      if (queue.end_date && moment(dateStr).isAfter(queue.end_date)) {
        // If we've passed the end date, return the next day anyway
        // The UI will handle showing appropriate error messages
        return nextDate.format('YYYY-MM-DD');
      }

      return dateStr;
    }

    nextDate.add(1, 'day');
    attempts++;
  }

  // Fallback: return next day if we couldn't find a valid date
  return moment(currentDate).add(1, 'day').format('YYYY-MM-DD');
};

/**
 * Gets the previous valid date based on queue schedule
 * @param {Object} queue - Queue object with schedule information
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @returns {string} - Previous valid date in YYYY-MM-DD format
 */
export const getPreviousValidDate = (queue, currentDate) => {
  if (!queue?.schedule?.weekdays) {
    // No schedule restrictions, just return previous day
    return moment(currentDate).subtract(1, 'day').format('YYYY-MM-DD');
  }

  const weekdays = queue.schedule.weekdays;
  const activeDays = Object.keys(weekdays)
    .filter((day) => !weekdays[day]?.is_closed)
    .map((day) => parseInt(day));

  if (activeDays.length === 0) {
    // No active days, just return previous day
    return moment(currentDate).subtract(1, 'day').format('YYYY-MM-DD');
  }

  let previousDate = moment(currentDate).subtract(1, 'day');
  let attempts = 0;
  const maxAttempts = 14; // Prevent infinite loop, check up to 2 weeks

  while (attempts < maxAttempts) {
    const momentWeekday = previousDate.weekday();
    const pythonWeekday = (momentWeekday + 6) % 7; // Convert to Python weekday

    // Check if this day is active in the schedule
    if (activeDays.includes(pythonWeekday)) {
      // Check if date is within queue's date range
      const dateStr = previousDate.format('YYYY-MM-DD');

      if (queue.end_date && moment(dateStr).isAfter(queue.end_date)) {
        previousDate.subtract(1, 'day');
        attempts++;
        continue;
      }

      if (queue.start_date && moment(dateStr).isBefore(queue.start_date)) {
        // If we've gone before the start date, return the previous day anyway
        // The UI will handle showing appropriate error messages
        return previousDate.format('YYYY-MM-DD');
      }

      return dateStr;
    }

    previousDate.subtract(1, 'day');
    attempts++;
  }

  // Fallback: return previous day if we couldn't find a valid date
  return moment(currentDate).subtract(1, 'day').format('YYYY-MM-DD');
};

/**
 * Unified date validation function that handles all queue constraints
 * @param {Object} queue - Queue object with schedule information
 * @param {string} date - Date to check in YYYY-MM-DD format
 * @param {Object} options - Validation options
 * @param {boolean} options.includeHolidays - Whether to check holiday overrides (default: true)
 * @param {boolean} options.includeAdvanceWindow - Whether to check advance booking window (default: false)
 * @param {boolean} options.allowPastDates - Whether to allow dates before today (default: false)
 * @returns {boolean} - True if date is valid for the queue
 */
export const isValidQueueDate = (queue, date, options = {}) => {
  const {
    includeHolidays = true,
    includeAdvanceWindow = false,
    allowPastDates = false,
  } = options;

  if (!queue || !date) return false;

  const checkDate = moment(date);
  const today = moment().format('YYYY-MM-DD');

  // Check if date is in the past (unless explicitly allowed)
  if (!allowPastDates && checkDate.isBefore(today)) {
    return false;
  }

  // Check advance booking window if enabled
  if (includeAdvanceWindow && queue.settings?.advance_appointment_window) {
    const maxAllowedDate = moment()
      .add(queue.settings.advance_appointment_window, 'days')
      .format('YYYY-MM-DD');
    if (checkDate.isAfter(maxAllowedDate)) {
      return false;
    }
  }

  // Check queue date range constraints
  if (queue.start_date && checkDate.isBefore(queue.start_date)) {
    return false;
  }

  if (queue.end_date && checkDate.isAfter(queue.end_date)) {
    return false;
  }

  // Check for specific date overrides/holidays first
  if (includeHolidays && queue.schedule?.dates && queue.schedule.dates[date]) {
    const dateSettings = queue.schedule.dates[date];
    return !dateSettings.is_closed;
  }

  // Check weekday schedule
  if (!queue?.schedule?.weekdays) {
    return true; // No schedule restrictions
  }

  const weekdays = queue.schedule.weekdays;
  // Queue weekdays use Python's date.weekday(): 0=Monday, 1=Tuesday, 2=Wednesday, 3=Thursday, 4=Friday, 5=Saturday, 6=Sunday
  // moment.weekday(): 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
  // Python weekday(): 0=Monday, 1=Tuesday, 2=Wednesday, 3=Thursday, 4=Friday, 5=Saturday, 6=Sunday
  // Convert moment.weekday() to Python weekday: Sunday(0)→6, Monday(1)→0, Tuesday(2)→1, etc.
  const momentWeekday = checkDate.weekday();
  const pythonWeekday = momentWeekday === 0 ? 6 : momentWeekday - 1;

  // Check if the day is active in the schedule
  const dayData = weekdays[pythonWeekday.toString()];

  if (!dayData || dayData.is_closed) {
    return false;
  }

  // For batch queues, check if batch configuration exists
  if (queue.type === 'Batch') {
    const hasBatchConfig = dayData.batch_size && dayData.batch_slot_duration;

    if (!hasBatchConfig) {
      return false;
    }
  }

  return true;
};

/**
 * Gets the nearest valid date from the current date (forward direction)
 * @param {Object} queue - Queue object with schedule information
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @returns {string} - Nearest valid date in YYYY-MM-DD format
 */
export const getNearestValidDate = (queue, currentDate) => {
  if (isValidQueueDate(queue, currentDate)) {
    return currentDate; // Current date is already valid
  }

  // Try to find the next valid date
  return getNextValidDate(queue, currentDate);
};

/**
 * Unified navigation function that finds valid dates in either direction
 * @param {Object} queue - Queue object with complete schedule information
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @param {string} direction - Direction to search: 'next' or 'previous'
 * @param {Object} options - Additional options
 * @param {number} options.maxDaysToCheck - Maximum days to search (default: 365)
 * @param {boolean} options.includeAdvanceWindow - Whether to respect advance booking window (default: false)
 * @param {boolean} options.allowPastDates - Whether to allow dates before today (default: false)
 * @returns {string|null} - Valid date in YYYY-MM-DD format, or null if none found
 */
export const findValidQueueDate = (
  queue,
  currentDate,
  direction = 'next',
  options = {}
) => {
  const {
    maxDaysToCheck = 365,
    includeAdvanceWindow = false,
    allowPastDates = false,
  } = options;

  if (!queue || !currentDate) return null;

  const isNext = direction === 'next';
  let searchDate = moment(currentDate)[isNext ? 'add' : 'subtract'](1, 'day');
  let attempts = 0;
  const today = moment().format('YYYY-MM-DD');

  while (attempts < maxDaysToCheck) {
    const dateStr = searchDate.format('YYYY-MM-DD');

    // Don't go before today unless explicitly allowed
    if (!allowPastDates && moment(dateStr).isBefore(today)) {
      return null;
    }

    // Check if it's a valid queue date with all constraints
    const validationOptions = {
      includeHolidays: true,
      includeAdvanceWindow,
      allowPastDates,
    };

    if (isValidQueueDate(queue, dateStr, validationOptions)) {
      return dateStr;
    }

    searchDate[isNext ? 'add' : 'subtract'](1, 'day');
    attempts++;
  }

  return null; // No valid date found within search range
};

// Backward compatibility - delegate to unified function
export const findNextValidQueueDate = (queue, currentDate, options = {}) => {
  return findValidQueueDate(queue, currentDate, 'next', options);
};

// Backward compatibility - delegate to unified function
export const findPreviousValidQueueDate = (
  queue,
  currentDate,
  options = {}
) => {
  return findValidQueueDate(queue, currentDate, 'previous', options);
};

/**
 * @deprecated Use isValidQueueDate with includeHolidays option instead
 * Enhanced date validation that considers holidays from queue data
 * @param {Object} queue - Queue object with schedule and holiday information
 * @param {string} date - Date to check in YYYY-MM-DD format
 * @returns {boolean} - True if date is valid for the queue
 */
export const isValidQueueDateWithHolidays = (queue, date) => {
  return isValidQueueDate(queue, date, { includeHolidays: true });
};

/**
 * Utility function for automatic date navigation logic
 * Can be used in useEffect within components
 * @param {Object} queue - Queue object with schedule information
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @param {Array} slots - Available slots for the current date
 * @param {Function} setDate - Function to update the date
 * @param {Object} options - Navigation options
 * @param {boolean} options.enabled - Whether auto-navigation is enabled (default: true)
 * @param {boolean} options.onlyForToday - Only auto-navigate if current date is today (default: true)
 * @returns {boolean} - True if date was changed
 */
export const handleAutoDateNavigation = (
  queue,
  currentDate,
  slots,
  setDate,
  options = {}
) => {
  const { enabled = true, onlyForToday = true } = options;

  if (!enabled || !queue || !currentDate || !setDate) return false;

  const today = moment().format('YYYY-MM-DD');
  const isToday = currentDate === today;

  // Only auto-advance if we're on today (unless disabled) and have no slots
  if (onlyForToday && !isToday) return false;
  if (slots && slots.length > 0) return false;

  // Check if current date is valid according to schedule
  if (!isValidQueueDate(queue, currentDate)) {
    const nearestDate = getNearestValidDate(queue, currentDate);
    if (nearestDate && nearestDate !== currentDate) {
      setDate(new Date(nearestDate));
      return true;
    }
    return false;
  }

  // If today has no slots, find next available date
  if (isToday && (!slots || slots.length === 0)) {
    const nextValidDate = findValidQueueDate(queue, currentDate, 'next');
    if (nextValidDate && nextValidDate !== currentDate) {
      setDate(new Date(nextValidDate));
      return true;
    }
  }

  return false;
};

/**
 * Determines if navigation buttons should be shown based on queue constraints
 * @param {Object} queue - Queue object with complete information
 * @param {string} currentDate - Current date in YYYY-MM-DD format
 * @param {Object} options - Navigation options
 * @param {boolean} options.includeAdvanceWindow - Whether to respect advance booking window (default: false)
 * @returns {Object} - Navigation availability { canShowNext: boolean, canShowPrevious: boolean }
 */
export const getQueueNavigationAvailability = (
  queue,
  currentDate,
  options = {}
) => {
  const { includeAdvanceWindow = false } = options;

  if (!queue || !currentDate) {
    return { canShowNext: false, canShowPrevious: false };
  }

  const today = moment().format('YYYY-MM-DD');

  const navigationOptions = {
    includeAdvanceWindow,
    allowPastDates: false,
  };

  const canShowPrevious =
    moment(currentDate).isAfter(today) &&
    findValidQueueDate(queue, currentDate, 'previous', navigationOptions) !==
      null;

  const canShowNext =
    findValidQueueDate(queue, currentDate, 'next', navigationOptions) !== null;

  return {
    canShowNext,
    canShowPrevious,
  };
};

/**
 * Helper function to format service price with flexible options
 * @param {number|string} price - The price value to format
 * @param {Object} options - Formatting options
 * @param {string} options.currency - Currency symbol or code (default: '₹')
 * @param {Function} options.t - Translation function (required for 'Free' text)
 * @param {number} options.decimalPlaces - Number of decimal places (default: 0)
 * @param {boolean} options.addSpace - Add space between currency and amount (default: false)
 * @param {boolean} options.useCurrencyCode - Use currency code format like 'INR 100' (default: false)
 * @returns {string} - Formatted price string
 */
export const formatServicePrice = (price, options = {}) => {
  const {
    currency = '₹',
    t,
    decimalPlaces = 0,
    addSpace = false,
    useCurrencyCode = false,
  } = options;

  // Handle null, undefined, zero, or '0' values
  if (!price || price === 0 || price === '0') {
    return t ? t('Free') : 'Free';
  }

  // Convert to number and format
  const numericPrice = parseFloat(price);
  const formattedPrice =
    decimalPlaces > 0
      ? numericPrice.toFixed(decimalPlaces)
      : numericPrice.toString();

  // Format with currency
  if (useCurrencyCode) {
    return `${currency} ${formattedPrice}`;
  }

  return addSpace
    ? `${currency} ${formattedPrice}`
    : `${currency}${formattedPrice}`;
};
